<template>
  <v-flex
    xs12
  >
    <v-btn
      @click="openExportDialog"
    >
      {{ $t('Export') }}
    </v-btn>
    <v-btn
      @click="openImportDialog"
    >
      {{ $t('Import') }}
    </v-btn>
    <template
      v-if="exportDialog"
    >
      <v-dialog
        v-model="exportDialog"
        persistent
        max-width="600px"
        scrollable
      >
        <v-card>
          <v-card-title>
            <h4>{{ $t('Products Export') }}</h4>
          </v-card-title>
          <v-divider/>
          <v-card-text>
            <v-switch
              v-model="withChildren"
              :label="$t('Include products children in exported file.')"
              class="pl-1"
            />
          </v-card-text>
          <v-divider/>
          <v-card-actions class="pa-2">
            <v-spacer />
            <v-btn
              color="darken-1"
              flat
              small
              @click="closeExportDialog"
            >
              {{ $t('Cancel') }}
            </v-btn>
            <v-btn
              :loading="exportingProducts"
              color="primary darken-1"
              flat
              small
              @click="exportProducts"
            >
              {{ $t('Export') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
    <template
      v-if="importDialog"
    >
      <v-dialog
        v-model="importDialog"
        persistent
        max-width="600px"
        scrollable
      >
        <v-card>
          <v-card-title>
            <h4>{{ $t('Products Import') }}</h4>
          </v-card-title>
          <v-divider/>
          <v-card-text>
            <template v-if="!hasErrors">
              <v-switch
                v-model="updateExisting"
                :label="$t('Update existing products.')"
                class="pl-1"
              />
              <v-text-field
                v-model="fileName"
                :label="$t('Select...')"
                prepend-icon="attach_file"
                @click="onPickFile"
              />
              <!-- Hidden -->
              <input
                ref="fileInput"
                type="file"
                style="display: none"
                accept="*/*"
                @change="onFilePicked"
              >
            </template>
            <v-layout
              v-if="hasErrors"
              class="pa-3"
              column
            >
              <v-flex class="my-3">{{ $t('Errors') }}</v-flex>
              <template v-for="(errors, index) in importErrors">
                <v-flex
                  :key="index"
                  class="font-weight-bold">
                  Row: {{ index }}
                </v-flex>
                <v-flex
                  :key="index + '_errors'"
                  class="red--text text-wrap"
                >
                  <span
                    v-for="(error, errIndex) in errors"
                    :key="errIndex">{{ error.join() }}
                  </span>
                </v-flex>
              </template>
            </v-layout>
          </v-card-text>
          <v-divider/>
          <v-card-actions
            v-if="!hasErrors"
            class="pa-2"
          >
            <v-btn
              :disabled="importingProducts"
              flat
              small
              @click="clearImport"
            >
              {{ $t('Clear') }}
            </v-btn>
            <v-spacer/>
            <v-btn
              color="darken-1"
              flat
              small
              @click="closeImportDialog"
            >
              {{ $t('Cancel') }}
            </v-btn>
            <v-btn
              :loading="importingProducts"
              color="primary darken-1"
              flat
              small
              @click="importProducts"
            >
              {{ $t('Import') }}
            </v-btn>
          </v-card-actions>
          <v-card-actions
            v-else
            class="pa-2"
          >
            <v-btn
              :loading="importingProducts"
              color="primary darken-1"
              flat
              small
              @click="closeImportDialog"
            >
              {{ $t('Close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </template>
  </v-flex>
</template>

<script>
export default {
  name: 'ProductExportImport',

  data () {
    return {
      product: this.$http.make('product'),
      withChildren: false,
      updateExisting: false,
      exportDialog: false,
      importDialog: false,
      fileName: '',
      fileObject: null,
      importErrors: {},
      importingProducts: false,
      exportingProducts: false
    }
  },

  computed: {
    hasErrors () {
      return Object.keys(this.importErrors).length > 0
    }
  },

  methods: {
    openExportDialog () {
      this.exportDialog = true
    },

    openImportDialog () {
      this.importDialog = true
    },

    closeExportDialog () {
      this.exportDialog = false
      this.exportingProducts = false
    },

    closeImportDialog () {
      this.importDialog = false
      this.clearImport()
    },

    clearImport () {
      this.importErrors = {}
      this.fileObject = null
      this.fileName = ''
      this.updateExisting = false
      this.importingProducts = false
    },

    exportProducts () {
      this.exportingProducts = true
      let options = {
        with_children: this.withChildren
      }

      this.product.doExport(options).then(response => {
        this.exportDialog = false
        this.withChildren = false
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', 'products.csv')
        document.body.appendChild(link)
        link.click()
        this.$toast(this.$t('Products exported. Please save the file.'))
      }).catch(e => {
        this.$toast.error(this.$t(e.message))
      }).finally(() => {
        this.exportingProducts = false
      })
    },

    importProducts () {
      this.importingProducts = true
      this.importErrors = []

      let form = new FormData()
      form.append('products', this.fileObject)
      form.append('update', this.updateExisting ? 1 : 0)

      this.product.doImport(form).then(() => {
        this.importDialog = false
        this.fileObject = null
        this.fileName = ''
        this.updateExisting = 0
        this.$emit('update-products-list')
        this.$toast(this.$t('Products imported.'))
      }).catch(e => {
        if (typeof e.response.data.errors === 'object') {
          this.$emit('update-products-list')
          this.importErrors = e.response.data.errors
        }
      }).finally(() => {
        this.importingProducts = false
      })
    },

    onPickFile () {
      this.$refs.fileInput.click()
    },

    onFilePicked (event) {
      const files = event.target.files
      if (files[0] === undefined) {
        this.fileObject = null
        this.fileName = ''
        return
      }

      this.fileName = files[0].name
      this.fileObject = files[0]
    }
  }
}
</script>
